import { Clear, ExpandMore, Search } from '@mui/icons-material';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Grid,
  IconButton,
  InputAdornment,
} from '@mui/material';
import { ReactComponent as DBTableIcon } from 'new/components/icons/fi-rr-table.svg';
import { TextField } from 'new/elements/TextField';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { Virtuoso } from 'react-virtuoso';

import './ConfigurationFields.scss';

// Helper function to get table display information
const getTableDisplayInfo = (tableName, config) => {
  const tableConfig = config?.['cdc.table.config'];
  const eltModeConfig = config?.['elt.mode.config'];

  // Check if table has resync enabled
  const resyncTables = tableConfig?.['resync.tables'] || [];
  const isResyncTable = resyncTables.includes(tableName);

  // Get table configuration from elt.mode.config
  const tableConfig_elt = eltModeConfig?.[tableName];
  const hasFilter = tableConfig_elt && tableConfig_elt.filter;

  // Get sync mode and column information from elt.mode.config
  let syncMode = 'Auto Increment Strategy'; // Default
  let columnInfo = '';
  let filterInfo = '';

  if (tableConfig_elt) {
    if (tableConfig_elt.mode === 'none') {
      syncMode = 'Full Ingest Strategy';
    } else if (tableConfig_elt.mode === 'incrementing') {
      syncMode = 'Auto Increment Strategy';
      columnInfo = tableConfig_elt['incrementing.column.name'] || '';
    } else if (tableConfig_elt.mode === 'timestamp') {
      syncMode = 'Auto Increment Strategy';
      columnInfo = tableConfig_elt['timestamp.column.name'] || '';
    }

    if (tableConfig_elt.filter) {
      filterInfo = tableConfig_elt.filter;
    }
  }

  return {
    syncMode,
    isResyncTable,
    hasFilter,
    filter: filterInfo,
    columnInfo,
    hasColumnInfo: !!columnInfo,
  };
};

// Optimized table row component for better performance
const VirtualizedTableRow = React.memo(({ table, index, config }) => {
  const displayInfo = useMemo(() => getTableDisplayInfo(table, config), [table, config]);

  return (
    <Grid container key={`table-${table || index}`} className="table-body-row">
      <Grid item xs={3}>
        <div className="table-cell-container">
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-md)' }}>
            <DBTableIcon style={{ width: 16, height: 16 }} />
            <span className="display-name">
              {table.length > 25 ? table.substring(0, 25) + '...' : table}
            </span>
          </div>
        </div>
      </Grid>
      {config?.['cdc.mode'] === 'database_read' && (
        <>
          <Grid item xs={8}>
            <div className="table-cell-container">
              <div>
                <span className="sync-mode">{displayInfo.syncMode}</span>
                {displayInfo.hasColumnInfo && (
                  <div
                    style={{
                      fontSize: '11px',
                      color: '#666',
                      marginTop: 'var(--spacing-xs)',
                    }}
                  >
                    Column: {displayInfo.columnInfo}
                  </div>
                )}
                {displayInfo.hasFilter && (
                  <div
                    style={{
                      fontSize: '11px',
                      color: '#666',
                      marginTop: 'var(--spacing-xs)',
                    }}
                  >
                    Filter: {displayInfo.filter}
                  </div>
                )}
              </div>
            </div>
          </Grid>
          <Grid item xs={1}>
            <div className="table-cell-container">
              <div className="auto-detected-indicator">
                {displayInfo.isResyncTable && (
                  <div className="auto-detected-dot" title="Re-sync enabled" />
                )}
              </div>
            </div>
          </Grid>
        </>
      )}
    </Grid>
  );
});

/**
 * CDCTablesListWithSearch component for displaying and filtering CDC tables
 * @param {Object} props
 * @param {Array} props.tables - Array of table names
 * @param {Object} props.config - Source configuration
 * @param {boolean} props.isExpanded - Whether the accordion is expanded
 * @param {Function} props.onAccordionToggle - Callback for accordion toggle
 */
const CDCTablesListWithSearch = ({ tables, config, isExpanded, onAccordionToggle }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [sortColumn, setSortColumn] = useState('name');
  const [sortDirection, setSortDirection] = useState('asc');
  const containerRef = useRef(null);
  const virtuosoRef = useRef(null);

  // Calculate table counts
  const tableCounts = useMemo(() => {
    let resync = 0;

    tables.forEach((table) => {
      const displayInfo = getTableDisplayInfo(table, config);
      if (displayInfo.isResyncTable) {
        resync++;
      }
    });

    return { resync, total: tables.length };
  }, [tables, config]);

  // Check if auto-include new tables is enabled
  const isAutoIncludeEnabled = useMemo(() => {
    const tableConfig = config?.['cdc.table.config'];
    return tableConfig?.['selection.mode'] === 'semi_auto';
  }, [config]);

  // Optimized sorting function
  const sortTables = useCallback(
    (tablesList, column, direction) => {
      const sortedTables = [...tablesList];

      return sortedTables.sort((a, b) => {
        let aValue = '';
        let bValue = '';

        switch (column) {
          case 'name':
            aValue = (a || '').toLowerCase();
            bValue = (b || '').toLowerCase();
            break;
          case 'syncMode':
            const aInfo = getTableDisplayInfo(a, config);
            const bInfo = getTableDisplayInfo(b, config);
            aValue = aInfo.syncMode.toLowerCase();
            bValue = bInfo.syncMode.toLowerCase();
            break;
          default:
            aValue = (a || '').toLowerCase();
            bValue = (b || '').toLowerCase();
        }

        if (direction === 'asc') {
          return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
        } else {
          return bValue < aValue ? -1 : bValue > aValue ? 1 : 0;
        }
      });
    },
    [config],
  );

  // Handle column sorting
  const handleColumnSort = useCallback(
    (column) => {
      if (sortColumn === column) {
        setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
      } else {
        setSortColumn(column);
        setSortDirection('asc');
      }
    },
    [sortColumn, sortDirection],
  );

  // Helper function to check if a table matches search term
  const isTableMatchingSearch = useCallback((table, searchLower) => {
    const tableName = table?.toLowerCase() || '';
    return tableName.includes(searchLower);
  }, []);

  // Immediate search with optimized filtering and sorting
  const filteredAndSortedTables = useMemo(() => {
    // Start with the base tables list
    let filtered = tables;

    // Apply search filter
    if (searchTerm.trim()) {
      const searchLower = searchTerm.trim().toLowerCase();
      filtered = filtered.filter((table) => {
        return isTableMatchingSearch(table, searchLower);
      });
    }

    // Sort by selected column and direction
    return sortTables(filtered, sortColumn, sortDirection);
  }, [tables, searchTerm, sortColumn, sortDirection, sortTables, isTableMatchingSearch]);

  // Helper function to get search results info text
  const getSearchResultsInfo = useCallback(() => {
    if (!searchTerm.trim() || !tables.length) return null;

    return `Showing ${filteredAndSortedTables.length} of ${tableCounts.total} tables`;
  }, [searchTerm, tableCounts.total, filteredAndSortedTables.length]);

  // Helper function to get search placeholder text
  const getSearchPlaceholder = useCallback(() => {
    return 'Search tables...';
  }, []);

  // Proper virtualization with Virtuoso
  const itemContent = useCallback(
    (index, table) => {
      return (
        <VirtualizedTableRow
          key={`table-${table || index}`}
          table={table}
          index={index}
          config={config}
        />
      );
    },
    [config],
  );

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleClearSearch = () => {
    setSearchTerm('');
  };

  return (
    <div className="details-accordion-group" aria-label="details-accordion-group-container">
      {/* Accordion Header */}
      <Accordion
        className="accordion-item"
        key="tables-accordion"
        expanded={isExpanded}
        onChange={onAccordionToggle}
      >
        <AccordionSummary className="accordion-title" expandIcon={<ExpandMore fontSize="large" />}>
          <div className="accordion-title-content">
            <div className="accordion-content-grow">
              <span className="title">Tables</span>
            </div>
            <div className="feeds-subtitle-above">
              {tableCounts.total} tables
              {isAutoIncludeEnabled && (
                <span
                  style={{
                    marginLeft: 'var(--spacing-md)',
                    color: '#666',
                    fontSize: 'var(--font-size-base-sm)',
                  }}
                >
                  • Automatically include new tables
                </span>
              )}
            </div>
            <div className="toggle-text" />
          </div>
        </AccordionSummary>

        <AccordionDetails className="accordion-body">
          <div className="feeds-list-wrapper">
            <div className="feeds-list-container">
              {/* Search Input and Filter Controls */}
              <div className="feeds-search-container">
                <div className="search-controls-inline">
                  {/* Search field */}
                  <div className="search-field-container">
                    <TextField
                      variant="outlined"
                      size="small"
                      placeholder={getSearchPlaceholder()}
                      value={searchTerm}
                      onChange={handleSearchChange}
                      className="search-field"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Search fontSize="small" />
                          </InputAdornment>
                        ),

                        endAdornment: (
                          <InputAdornment position="end">
                            {searchTerm && (
                              <IconButton size="small" onClick={handleClearSearch} edge="end">
                                <Clear fontSize="small" />
                              </IconButton>
                            )}
                          </InputAdornment>
                        ),
                      }}
                    />
                  </div>
                </div>

                {/* Results count positioned below search controls */}
                {getSearchResultsInfo() && (
                  <div className="search-results-info">{getSearchResultsInfo()}</div>
                )}
              </div>

              {/* Tables Table */}
              <div className="full-width-table">
                <div className="table-header">
                  <Grid container className="table-header-row">
                    <Grid item xs={3}>
                      <div className="sortable-header" onClick={() => handleColumnSort('name')}>
                        Table Name
                        {sortColumn === 'name' && (
                          <span className="sort-indicator">
                            {sortDirection === 'asc' ? '↑' : '↓'}
                          </span>
                        )}
                      </div>
                    </Grid>
                    {config?.['cdc.mode'] === 'database_read' && (
                      <>
                        <Grid item xs={8}>
                          <div
                            className="sortable-header"
                            onClick={() => handleColumnSort('syncMode')}
                          >
                            Ingestion Mode
                            {sortColumn === 'syncMode' && (
                              <span className="sort-indicator">
                                {sortDirection === 'asc' ? '↑' : '↓'}
                              </span>
                            )}
                          </div>
                        </Grid>
                        <Grid item xs={1}>
                          <div className="header-text">Re-sync</div>
                        </Grid>
                      </>
                    )}
                  </Grid>
                </div>
                <div className="table-body" ref={containerRef}>
                  <Virtuoso
                    ref={virtuosoRef}
                    key="tables-list-table"
                    data={filteredAndSortedTables}
                    itemContent={itemContent}
                    customScrollParent={containerRef.current}
                    className="virtuoso-container"
                    style={{ height: '400px' }}
                    components={{
                      Footer: () => <div className="virtuoso-footer"></div>,
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        </AccordionDetails>
      </Accordion>
    </div>
  );
};

export default CDCTablesListWithSearch;
